import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Fab,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  useTheme,
  useMediaQuery,
  Checkbox,
  Toolbar,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  PowerSettingsNew as DeactivateIcon,
  PlayCircle as ReactivateIcon,
  DeleteSweep as DeleteSweepIcon,
  PowerSettingsNew as BulkDeactivateIcon,
  SelectAll as SelectAllIcon,
  Close as CloseIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useNotifications } from '../../context/NotificationContext';
import { useActivitySuspension } from '../../hooks/useActivitySuspension';
import { useRestrictionBanner } from '../../hooks/useRestrictions';
import { getTokenAuth } from '../../utils/authUtils';
import ListingProductSidebar from '../common/ListingProductSidebar';
import CreateChoiceModal from '../common/CreateChoiceModal';
import {
  DEACTIVATION_TYPES,
  getDeactivationReasons,
  getDeactivationLabel
} from '../../constants/deactivationTypes';

// Fonction utilitaire pour vérifier un UUID v4
function isValidUUID(uuid) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(uuid);
}

const MyListings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatPrice: formatCurrencyPrice } = useCurrency();
  const { showNotification } = useNotifications();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isSuspended: userIsActivitySuspended } = useActivitySuspension();
  const { isBanned, banType } = useRestrictionBanner();
  const userIsBanned = !!(isBanned && (banType === 'permanent' || banType === 'temporary'));
  
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [listingToDelete, setListingToDelete] = useState(null);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [listingToDeactivate, setListingToDeactivate] = useState(null);
  const [deactivateReasonText, setDeactivateReasonText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [listingToReactivate, setListingToReactivate] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const debounceRef = useRef(null);
  const genRef = useRef(0);

  const LIMIT = 12;

  // ── Bulk actions state ──────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeactivateDialogOpen, setBulkDeactivateDialogOpen] = useState(false);
  const [bulkDeactivateReason, setBulkDeactivateReason] = useState('');
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkMode, setBulkMode] = useState(null); // null | 'deactivate' | 'delete'
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('DESC');
  
  // ── État pour le modal de choix création ──────────────────────────────────
  const [createChoiceModalOpen, setCreateChoiceModalOpen] = useState(false);

  useEffect(() => {
    fetchListings(page, searchFilter, statusFilter, sortBy, sortDir);
  }, [page, statusFilter, sortBy, sortDir]);

  const fetchListings = async (currentPage, search = '', status = '', currentSortBy = 'created_at', currentSortDir = 'DESC') => {
    const gen = ++genRef.current;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search?.trim()) params.append('search', search.trim());
      if (status) params.append('status', status);
      params.append('page', currentPage);
      params.append('limit', LIMIT);
      params.append('sortBy', currentSortBy);
      params.append('sortDir', currentSortDir);
      const response = await fetch(`/api/v1/listings/my-listings?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${getTokenAuth()}`
        }
      });
      if (gen !== genRef.current) return;
      if (!response.ok) {
        let backendMsg = '';
        try {
          const errData = await response.json();
          backendMsg = errData.message || JSON.stringify(errData);
        } catch (parseErr) {
          backendMsg = 'Réponse non JSON ou vide';
        }
        const fullMsg = `Erreur HTTP ${response.status} : ${response.statusText} - ${backendMsg}`;
        setErrorMsg(fullMsg);
        showNotification(fullMsg, 'error');
        throw new Error(fullMsg);
      }
      const data = await response.json();
      setListings(data.listings || []);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.totalCount || 0);
      setErrorMsg('');
    } catch (error) {
      if (gen !== genRef.current) return;
      setErrorMsg(error.message || 'Erreur inconnue');
      showNotification(error.message || t('listing.errors.fetchFailed'), 'error');
    } finally {
      if (gen !== genRef.current) return;
      setLoading(false);
    }
  };

  const updateCountAfterDelete = (delta) => {
    setTotalCount(prev => {
      const newCount = Math.max(0, prev - delta);
      const newTotalPages = Math.max(1, Math.ceil(newCount / LIMIT));
      setTotalPages(newTotalPages);
      setPage(p => (p > newTotalPages ? newTotalPages : p));
      return newCount;
    });
  };

  const handleSearchChange = (value) => {
    setSearchFilter(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchListings(1, value, statusFilter, sortBy, sortDir);
    }, 400);
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const handleDelete = async (listingId) => {
    try {
      const response = await fetch(`/api/v1/listings/${listingId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getTokenAuth()}`
        }
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 409 && data.code === 'LISTING_HAS_RESERVATIONS') {
        showNotification(
          t('listing.errors.cannotDeleteWithReservations', { count: data.reservationCount }),
          'error'
        );
        setDeleteDialogOpen(false);
        setListingToDelete(null);
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete listing');
      }

      setListings(prev => prev.filter(listing => listing.id !== listingId));
      updateCountAfterDelete(1);
      showNotification(t('listing.delete.successMessage', 'Annonce supprimée avec succès'), 'success');
    } catch (error) {
      console.error('Error deleting listing:', error);
      showNotification(error.message || t('listing.errors.deleteFailed'), 'error');
    }
    setDeleteDialogOpen(false);
    setListingToDelete(null);
  };

  const openDeleteDialog = (listing) => {
    setListingToDelete(listing);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setListingToDelete(null);
  };

  const openDeactivateDialog = (listing) => {
    setListingToDeactivate(listing);
    setDeactivateDialogOpen(true);
    setDeactivateReasonText('');
  };

  const closeDeactivateDialog = () => {
    setDeactivateDialogOpen(false);
    setListingToDeactivate(null);
    setDeactivateReasonText('');
  };

  const handleDeactivateSubmit = async () => {
    if (!deactivateReasonText.trim()) {
      showNotification('Veuillez spécifier une raison pour la désactivation.', 'warning');
      return;
    }

    try {
      setSubmitting(true);

      const listingId = listingToDeactivate.id;
      const reasonToUse = deactivateReasonText.trim();

      const response = await fetch(`/api/v1/owner-listing-management/${listingId}/deactivate-owned`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getTokenAuth()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason: reasonToUse,
          deactivationType: DEACTIVATION_TYPES.TEMPORARY
        })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Erreur lors de la désactivation');
      }

      showNotification('Annonce désactivée avec succès', 'success');

      setListings(prevListings => 
        prevListings.map(listing => 
          listing.id === listingId ? { ...listing, status: 'inactive' } : listing
        )
      );

      closeDeactivateDialog();
    } catch (error) {
      console.error('[MyListings] Error deactivating listing:', error);
      showNotification(error.message || 'Erreur lors de la désactivation de l\'annonce', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Bulk selection helpers ───────────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Sélection selon le mode actif
  const selectableListings = bulkMode === 'deactivate'
    ? listings
    : bulkMode === 'delete'
      ? listings.filter(l => !l.has_reservations)
      : [];
  const allSelected = selectableListings.length > 0 && selectableListings.every(l => selectedIds.has(l.id));
  const someSelected = selectableListings.some(l => selectedIds.has(l.id)) && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableListings.map(l => l.id)));
    }
  };

  // ── Bulk deactivate handler ─────────────────────────────────────────────────
  const handleBulkDeactivate = async () => {
    if (!bulkDeactivateReason.trim() || bulkDeactivateReason.trim().length < 5) {
      showNotification(t('listing.myListings.bulk.reasonTooShort', 'Le motif est trop court (min 5 caractères).'), 'error');
      return;
    }
    try {
      setBulkSubmitting(true);
      const ids = Array.from(selectedIds);
      const response = await fetch('/api/v1/owner-listing-management/bulk-deactivate', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getTokenAuth()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ids, reason: bulkDeactivateReason.trim(), deactivationType: DEACTIVATION_TYPES.TEMPORARY })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Erreur lors de la désactivation groupée');

      showNotification(
        t('listing.myListings.bulk.deactivateSuccess', { count: data.deactivated }),
        'success'
      );
      setListings(prev => prev.map(l => ids.includes(l.id) ? { ...l, status: 'inactive' } : l));
      setSelectedIds(new Set());
      setBulkDeactivateDialogOpen(false);
      setBulkDeactivateReason('');
    } catch (error) {
      showNotification(error.message || 'Erreur', 'error');
    } finally {
      setBulkSubmitting(false);
    }
  };

  // ── Bulk delete handler ─────────────────────────────────────────────────────
  const handleBulkDelete = async () => {
    try {
      setBulkSubmitting(true);
      const ids = Array.from(selectedIds);
      const response = await fetch('/api/v1/owner-listing-management/bulk-delete', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getTokenAuth()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ids })
      });
      const data = await response.json();

      if (response.status === 409 && data.code === 'SOME_LISTINGS_HAVE_RESERVATIONS') {
        showNotification(data.message, 'error');
        setBulkDeleteDialogOpen(false);
        setBulkSubmitting(false);
        return;
      }
      if (!response.ok) throw new Error(data.message || 'Erreur lors de la suppression groupée');

      showNotification(
        t('listing.myListings.bulk.deleteSuccess', { count: data.deleted }),
        'success'
      );
      setListings(prev => prev.filter(l => !ids.includes(l.id)));
      updateCountAfterDelete(data.deleted ?? ids.length);
      setSelectedIds(new Set());
      setBulkDeleteDialogOpen(false);
      setBulkMode(null);
    } catch (error) {
      showNotification(error.message || 'Erreur', 'error');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const openReactivateDialog = (listing) => {
    if (userIsBanned) {
      showNotification(
        t('listing.reactivate.bannedCannot', 'Vous ne pouvez pas réactiver tant que votre compte est banni'),
        'warning'
      );
      return;
    }

    setListingToReactivate(listing);
    setReactivateDialogOpen(true);
  };

  const closeReactivateDialog = () => {
    setReactivateDialogOpen(false);
    setListingToReactivate(null);
  };

  const handleReactivateSubmit = async () => {
    try {
      setSubmitting(true);
      const listingId = listingToReactivate.id;
      const response = await fetch(`/api/v1/owner-listing-management/${listingId}/reactivate-owned`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getTokenAuth()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || t('listing.reactivate.error', 'Erreur lors de la réactivation'));
      }
      showNotification(t('listing.reactivate.successMessage', 'Annonce réactivée avec succès'), 'success');
      setListings(prevListings =>
        prevListings.map(l =>
          l.id === listingId ? { ...l, status: 'active' } : l
        )
      );
      closeReactivateDialog();
    } catch (error) {
      console.error('[MyListings] Error reactivating listing:', error);
      showNotification(error.message || t('listing.reactivate.error', 'Erreur lors de la réactivation'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'draft':
        return 'default';
      case 'pending_verification':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return t('listing.status.active');
      case 'draft':
        return t('listing.status.draft');
      case 'pending_verification':
        return t('listing.status.pendingVerification');
      case 'rejected':
        return t('listing.status.rejected');
      case 'inactive':
        return t('listing.status.inactive');
      default:
        return status;
    }
  };

  const formatPrice = (price, currency = null) => {
    if (!price) return 'N/A';
    
    // Si la devise de l'annonce est fournie, l'utiliser
    // Sinon, utiliser la devise actuelle de l'utilisateur
    if (currency) {
      // Formater avec la devise spécifique de l'annonce
      try {
        return new Intl.NumberFormat('fr-FR', {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).format(price);
      } catch (error) {
        // Si la devise n'est pas supportée, revenir au format par défaut
        return formatCurrencyPrice(price);
      }
    }
    
    // Utiliser le formatage de devise de l'utilisateur
    return formatCurrencyPrice(price);
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return t('common.notAvailable');
    }
    try {
      const date = new Date(dateString);
      // Vérifier si la date est valide
      if (isNaN(date.getTime())) {
        return t('common.notAvailable');
      }
      // Format français : jour/mois/année
      const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      };
      return date.toLocaleDateString('fr-FR', options);
    } catch (error) {
      console.error('Error formatting date:', error);
      return t('common.notAvailable');
    }
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return t('listing.date.today');
      if (diffDays === 1) return t('listing.date.yesterday');
      if (diffDays < 7) return t('listing.date.daysAgo', { count: diffDays });
      if (diffDays < 30) return t('listing.date.weeksAgo', { count: Math.ceil(diffDays / 7) });
      if (diffDays < 365) return t('listing.date.monthsAgo', { count: Math.ceil(diffDays / 30) });
      return t('listing.date.yearsAgo', { count: Math.ceil(diffDays / 365) });
    } catch (error) {
      return '';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3, width: '100%' }}>
      {/* Header avec style amélioré */}
      {/* Header avec gradient */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 4,
          p: 3,
          borderRadius: 2,
          background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
          color: 'white',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            {t('listing.myListings.title')}{" "}
              {totalCount > 0 && `(${totalCount})`}
          </Typography>
           
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            {t('listing.myListings.description', 'Gérez vos annonces de location, services et covoiturage. Consultez leurs statuts, disponibilités et les réservations associées.')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateChoiceModalOpen(true)}
            sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white',
              fontWeight: 'medium',
              px: 3,
              py: 1.5,
              '&:hover': { 
                bgcolor: 'rgba(255,255,255,0.3)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {t('listing.myListings.createNew')}
          </Button>
          
        </Box>
      </Box>

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMsg}
        </Alert>
      )}

      {/* ── Barre d'actions groupées (apparaît quand ≥1 sélectionné) ─── */}
      {selectedIds.size > 0 && (
        <Paper
          elevation={3}
          sx={{
            mb: 2,
            borderRadius: 2,
            bgcolor: 'primary.dark',
            color: 'white',
            overflow: 'hidden'
          }}
        >
          <Toolbar sx={{ gap: 2, minHeight: '56px !important' }}>
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={toggleSelectAll}
              sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' } }}
            />
            <Typography sx={{ flexGrow: 1, fontWeight: 'bold', color: 'white' }}>
              {t('listing.myListings.bulk.selected', { count: selectedIds.size })}
            </Typography>
            {bulkMode === 'deactivate' && (
              <Tooltip title={t('listing.myListings.bulk.deactivate')}>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<BulkDeactivateIcon />}
                    onClick={() => setBulkDeactivateDialogOpen(true)}
                    sx={{
                      bgcolor: 'warning.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'warning.dark' },
                      textTransform: 'none',
                      fontWeight: 'bold'
                    }}
                  >
                    {t('listing.myListings.bulk.deactivate')}
                  </Button>
                </span>
              </Tooltip>
            )}
            {bulkMode === 'delete' && (
              <Tooltip title={t('listing.myListings.bulk.delete')}>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<DeleteSweepIcon />}
                    onClick={() => setBulkDeleteDialogOpen(true)}
                    sx={{
                      bgcolor: 'error.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'error.dark' },
                      textTransform: 'none',
                      fontWeight: 'bold'
                    }}
                  >
                    {t('listing.myListings.bulk.delete')}
                  </Button>
                </span>
              </Tooltip>
            )}
            <Tooltip title={t('listing.myListings.bulk.deselectAll')}>
              <IconButton onClick={() => { setSelectedIds(new Set()); setBulkMode(null); }} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </Paper>
      )}

      {/* ── Barre de filtres ─── */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5} md={4} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {bulkMode === null ? (
              <>
                <Tooltip title={t('listing.myListings.bulk.deactivate')}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<BulkDeactivateIcon />}
                    onClick={() => { setBulkMode('deactivate'); setSelectedIds(new Set()); }}
                    disabled={listings.length === 0}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('listing.myListings.bulk.deactivate')}
                  </Button>
                </Tooltip>
                <Tooltip title={t('listing.myListings.bulk.delete')}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteSweepIcon />}
                    onClick={() => { setBulkMode('delete'); setSelectedIds(new Set()); }}
                    disabled={listings.length === 0}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('listing.myListings.bulk.delete')}
                  </Button>
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip title={allSelected ? t('listing.myListings.bulk.deselectAll') : t('listing.myListings.bulk.selectAll')}>
                  <span>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={toggleSelectAll}
                      disabled={selectableListings.length === 0}
                    />
                  </span>
                </Tooltip>
                <Tooltip title={t('listing.myListings.bulk.cancel', 'Annuler')}>
                  <IconButton size="small" onClick={() => { setBulkMode(null); setSelectedIds(new Set()); }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              placeholder={t('listing.myListings.searchPlaceholder', 'Rechercher une annonce...')}
              size="small"
              fullWidth
              value={searchFilter}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>{t('listing.myListings.filterByStatus', 'Statut')}</InputLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusChange}
                label={t('listing.myListings.filterByStatus', 'Statut')}
              >
                <MenuItem value="">{t('listing.status.all', 'Tous')}</MenuItem>
                <MenuItem value="active">{t('listing.status.active')}</MenuItem>
                <MenuItem value="pending_verification">{t('listing.status.pendingVerification')}</MenuItem>
                <MenuItem value="rejected">{t('listing.status.rejected')}</MenuItem>
                <MenuItem value="inactive">{t('listing.status.inactive')}</MenuItem>
                <MenuItem value="draft">{t('listing.status.draft')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>{t('listing.myListings.sortBy', 'Trier par')}</InputLabel>
              <Select
                value={`${sortBy}|${sortDir}`}
                onChange={(e) => {
                  const sep = e.target.value.lastIndexOf('|');
                  const field = e.target.value.slice(0, sep);
                  const dir = e.target.value.slice(sep + 1);
                  setSortBy(field);
                  setSortDir(dir);
                  setPage(1);
                }}
                label={t('listing.myListings.sortBy', 'Trier par')}
              >
                <MenuItem value="created_at|DESC">{t('listing.myListings.sort.createdDesc', 'Plus récent d\'abord')}</MenuItem>
                <MenuItem value="created_at|ASC">{t('listing.myListings.sort.createdAsc', 'Plus ancien d\'abord')}</MenuItem>
                <MenuItem value="price_daily|DESC">{t('listing.myListings.sort.priceDesc', 'Prix décroissant')}</MenuItem>
                <MenuItem value="price_daily|ASC">{t('listing.myListings.sort.priceAsc', 'Prix croissant')}</MenuItem>
                <MenuItem value="title|ASC">{t('listing.myListings.sort.titleAsc', 'Titre A → Z')}</MenuItem>
                <MenuItem value="title|DESC">{t('listing.myListings.sort.titleDesc', 'Titre Z → A')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {listings.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 4,
            borderRadius: 3,
            background: 'linear-gradient(145deg, #f8f9ff 0%, #f0f2ff 100%)',
            border: '2px dashed #e0e7ff',
            minHeight: 400,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Box 
            sx={{ 
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)'
            }}
          >
            <AddIcon sx={{ fontSize: 60, color: 'white' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: 'text.primary' }}>
            {t('listing.myListings.noListings')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400 }}>
            {t('listing.myListings.createFirstDescription')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateChoiceModalOpen(true)}
            sx={{ 
              mt: 2,
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 'medium',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 25px rgba(102, 126, 234, 0.5)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {t('listing.myListings.createFirst')}
          </Button>
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
          {listings.map((listing) => (
            <Grid item xs={12} sm={6} lg={4} xl={3} key={listing.id}>
              <Box
                sx={{
                  position: 'relative',
                  height: '100%',
                  cursor: 'pointer',
                  '&:hover .listing-actions-overlay': { opacity: 1, pointerEvents: 'auto' },
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  },
                  transition: 'all 0.3s ease-in-out',
                  display: 'flex',
                  flexDirection: 'column',
                  outline: selectedIds.has(listing.id) ? '2px solid' : 'none',
                  outlineColor: 'primary.main',
                  borderRadius: 2,
                }}
                onClick={e => {
                  // Empêche le clic sur la carte si on clique sur un bouton d'action
                  if (
                    e.target.closest('.listing-actions-overlay') ||
                    e.target.closest('.MuiIconButton-root') ||
                    e.target.closest('.MuiCheckbox-root')
                  ) return;
                  if (isValidUUID(listing.id)) {
                    navigate(`/listing/${listing.id}`);
                  }
                }}
              >
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', boxShadow: 'none', flexGrow: 1 }}>
                  {/* Checkbox de sélection — visible uniquement si un mode bulk est actif */}
                  {bulkMode !== null && (
                    <Box sx={{ position: 'absolute', top: 4, left: 4, zIndex: 20 }}>
                      {bulkMode === 'delete' && listing.has_reservations ? (
                        <Tooltip
                          title={t('listing.myListings.bulk.cannotSelectHasReservations', 'Suppression impossible : des réservations sont liées à cette annonce')}
                          placement="top"
                        >
                          <span
                            onClick={e => e.stopPropagation()}
                            onMouseDown={e => e.stopPropagation()}
                            style={{ display: 'inline-flex' }}
                          >
                            <Checkbox
                              checked={false}
                              disabled
                              sx={{
                                bgcolor: 'rgba(211,47,47,0.15)',
                                borderRadius: '4px',
                                p: 0.5,
                                color: 'error.main',
                                '&.Mui-disabled': { color: 'error.main', opacity: 1 }
                              }}
                              size="small"
                            />
                          </span>
                        </Tooltip>
                      ) : (
                        <span
                          onClick={e => e.stopPropagation()}
                          onMouseDown={e => e.stopPropagation()}
                          style={{ display: 'inline-flex' }}
                        >
                          <Checkbox
                            checked={selectedIds.has(listing.id)}
                            onChange={() => toggleSelect(listing.id)}
                            sx={{
                              bgcolor: 'rgba(255,255,255,0.85)',
                              borderRadius: '4px',
                              p: 0.5,
                              '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
                            }}
                            size="small"
                          />
                        </span>
                      )}
                    </Box>
                  )}
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={listing.images?.[0]?.url || '/assets/placeholder-equipment.svg'}
                      alt={listing.title}
                      sx={{ objectFit: 'cover' }}
                    />
                    <Box 
                      sx={{ 
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        display: 'flex',
                        gap: 1
                      }}
                    >
                      <Chip
                        label={getStatusText(listing.status)}
                        color={getStatusColor(listing.status)}
                        size="small"
                        sx={{ 
                          fontWeight: 'medium',
                          '& .MuiChip-label': { px: 1.5 }
                        }}
                      />
                    </Box>
                  </Box>
                  <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    <Typography 
                      variant="h6" 
                      component="h2" 
                      sx={{ 
                        mb: 1,
                        fontWeight: 'medium',
                        lineHeight: 1.3,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {listing.title}
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        minHeight: '2.5em'
                      }}
                    >
                      {listing.description?.substring(0, 100)}...
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                        📍 {listing.city}
                      </Typography>
                      <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                        {formatPrice(listing.priceDaily, listing.currency)}
                      </Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t('listing.detail.perDay')}
                    </Typography>

                    {/* Désactivé suite à un ban */}
                    {userIsBanned && (
                      <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>
                        <Typography variant="caption">
                          {t('listing.disabledBanNotice')}
                        </Typography>
                      </Alert>
                    )}

                    {/* Notice suspension volontaire d'activité */}
                    {userIsActivitySuspended && listing.status === 'inactive' && (
                      <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                        <Typography variant="caption">
                          {t('activitySuspension.cardNotice')}
                        </Typography>
                      </Alert>
                    )}

                    {/* Informations de date améliorées */}
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        p: 1,
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        mt: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('listing.card.created')}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.75rem' }}>
                          {formatDate(listing.created_at)}
                        </Typography>
                        <Typography variant="caption" color="primary">
                          {getTimeAgo(listing.created_at)}
                        </Typography>
                      </Box>
                      {listing.updated_at && listing.updated_at !== listing.created_at && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('listing.card.updated')}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.75rem' }}>
                            {formatDate(listing.updated_at)}
                          </Typography>
                          <Typography variant="caption" color="secondary">
                            {getTimeAgo(listing.updated_at)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                  {/* Barre d'actions flottante au survol (desktop uniquement) */}
                  <Box
                    className="listing-actions-overlay"
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      display: { xs: 'none', sm: 'flex' },
                      gap: 2,
                      bgcolor: 'rgba(255,255,255,0.95)',
                      borderRadius: 2,
                      boxShadow: 3,
                      p: 1.5,
                      opacity: 0,
                      pointerEvents: 'none',
                      transition: 'opacity 0.2s',
                      zIndex: 10,
                      alignItems: 'center',
                    }}
                  >
                    <IconButton
                      size="large"
                      onClick={e => { e.stopPropagation(); navigate(`/edit-listing/${listing.id}`); }}
                      title={t('common.edit')}
                      sx={{
                        bgcolor: 'info.light',
                        color: 'white',
                        '&:hover': { bgcolor: 'info.main' },
                        mx: 1
                      }}
                    >
                      <EditIcon fontSize="medium" />
                    </IconButton>
                    {listing.status !== 'inactive' ? (
                      <IconButton
                        size="large"
                        onClick={e => { e.stopPropagation(); openDeactivateDialog(listing); }}
                        title={t('listing.actions.deactivate', 'Désactiver')}
                        sx={{
                          bgcolor: 'warning.light',
                          color: 'white',
                          '&:hover': { bgcolor: 'warning.main' },
                          mx: 1
                        }}
                      >
                        <DeactivateIcon fontSize="medium" />
                      </IconButton>
                    ) : (
                      <IconButton
                        size="large"
                        onClick={e => { e.stopPropagation(); openReactivateDialog(listing); }}
                        title={
                          userIsBanned
                            ? t('listing.reactivate.disabledReason', 'Impossible : compte banni')
                            : t('listing.actions.reactivate', 'Réactiver')
                        }
                        disabled={userIsBanned}
                        sx={{
                          bgcolor: userIsBanned ? 'grey.400' : 'success.light',
                          color: 'white',
                          '&:hover': { bgcolor: userIsBanned ? 'grey.400' : 'success.main' },
                          mx: 1
                        }}
                      >
                        <ReactivateIcon fontSize="medium" />
                      </IconButton>
                    )}
                    <IconButton
                      size="large"
                      onClick={e => { e.stopPropagation(); openDeleteDialog(listing); }}
                      title={listing.has_reservations
                        ? t('listing.myListings.bulk.cannotSelectHasReservations', 'Suppression impossible : des réservations sont liées')
                        : t('common.delete')
                      }
                      disabled={listing.has_reservations}
                      sx={{
                        bgcolor: listing.has_reservations ? 'grey.300' : 'error.light',
                        color: listing.has_reservations ? 'grey.500' : 'white',
                        '&:hover': { bgcolor: listing.has_reservations ? 'grey.300' : 'error.main' },
                        mx: 1
                      }}
                    >
                      <DeleteIcon fontSize="medium" />
                    </IconButton>
                  </Box>
                </Card>
                {/* Barre d'actions visible en permanence sur mobile */}
                {isMobile && (
                  <CardActions
                    sx={{
                      justifyContent: 'flex-end',
                      gap: 0.5,
                      px: 1,
                      py: 0.5,
                      bgcolor: 'grey.50',
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <IconButton
                      size="medium"
                      onClick={e => { e.stopPropagation(); navigate(`/edit-listing/${listing.id}`); }}
                      title={t('common.edit')}
                      sx={{ bgcolor: 'info.light', color: 'white', '&:hover': { bgcolor: 'info.main' } }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {listing.status !== 'inactive' ? (
                      <IconButton
                        size="medium"
                        onClick={e => { e.stopPropagation(); openDeactivateDialog(listing); }}
                        title={t('listing.actions.deactivate', 'Désactiver')}
                        sx={{ bgcolor: 'warning.light', color: 'white', '&:hover': { bgcolor: 'warning.main' } }}
                      >
                        <DeactivateIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <IconButton
                        size="medium"
                        onClick={e => { e.stopPropagation(); openReactivateDialog(listing); }}
                        title={
                          userIsBanned
                            ? t('listing.reactivate.disabledReason', 'Impossible : compte banni')
                            : t('listing.actions.reactivate', 'Réactiver')
                        }
                        disabled={userIsBanned}
                        sx={{ bgcolor: userIsBanned ? 'grey.400' : 'success.light', color: 'white', '&:hover': { bgcolor: userIsBanned ? 'grey.400' : 'success.main' } }}
                      >
                        <ReactivateIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton
                      size="medium"
                      onClick={e => { e.stopPropagation(); openDeleteDialog(listing); }}
                      title={listing.has_reservations
                        ? t('listing.myListings.bulk.cannotSelectHasReservations', 'Suppression impossible : des réservations sont liées')
                        : t('common.delete')
                      }
                      disabled={listing.has_reservations}
                      sx={{ bgcolor: listing.has_reservations ? 'grey.300' : 'error.light', color: listing.has_reservations ? 'grey.500' : 'white', '&:hover': { bgcolor: listing.has_reservations ? 'grey.300' : 'error.main' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </CardActions>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
            />
          </Box>
        )}
        </>
      )}

      {/* ── Bulk Deactivate Dialog ─── */}
      <Dialog
        open={bulkDeactivateDialogOpen}
        onClose={() => { setBulkDeactivateDialogOpen(false); setBulkDeactivateReason(''); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', borderBottom: '1px solid', borderColor: 'divider' }}>
          {t('listing.myListings.bulk.deactivateTitle', { count: selectedIds.size })}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('listing.myListings.bulk.deactivateInfo')}
          </Alert>
          <TextField
            fullWidth
            label={t('listing.deactivate.reasonLabel', 'Motif de désactivation')}
            placeholder={t('listing.deactivate.reasonPlaceholder', 'Ex: En attente de maintenance')}
            value={bulkDeactivateReason}
            onChange={e => setBulkDeactivateReason(e.target.value)}
            multiline
            rows={3}
            variant="outlined"
            required
            size="small"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            onClick={() => { setBulkDeactivateDialogOpen(false); setBulkDeactivateReason(''); }}
            disabled={bulkSubmitting}
            sx={{ textTransform: 'none' }}
          >
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button
            onClick={handleBulkDeactivate}
            color="warning"
            variant="contained"
            disabled={bulkSubmitting || bulkDeactivateReason.trim().length < 5}
            sx={{ textTransform: 'none', minWidth: 140 }}
          >
            {bulkSubmitting ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            {t('listing.myListings.bulk.deactivateConfirm', { count: selectedIds.size })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk Delete Dialog ─── */}
      <Dialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', borderBottom: '1px solid', borderColor: 'divider' }}>
          {t('listing.myListings.bulk.deleteTitle', { count: selectedIds.size })}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('listing.myListings.bulk.deleteWarning')}
          </Alert>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('listing.myListings.bulk.deleteReservationWarning')}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            {t('listing.myListings.bulk.deleteCount', { count: selectedIds.size })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            onClick={() => setBulkDeleteDialogOpen(false)}
            disabled={bulkSubmitting}
            sx={{ textTransform: 'none' }}
          >
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button
            onClick={handleBulkDelete}
            color="error"
            variant="contained"
            disabled={bulkSubmitting}
            sx={{ textTransform: 'none', minWidth: 140 }}
          >
            {bulkSubmitting ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            {t('listing.myListings.bulk.deleteConfirm', { count: selectedIds.size })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>
          {t('listing.delete.confirmTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('listing.delete.confirmMessage', { title: listingToDelete?.title })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => handleDelete(listingToDelete?.id)}
            color="error"
            variant="contained"
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivation Confirmation Dialog - Simple & Elegant */}
      <Dialog 
        open={deactivateDialogOpen} 
        onClose={closeDeactivateDialog} 
        maxWidth="xs" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 'bold', 
          fontSize: '1.1rem',
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          {t('listing.deactivate.confirmTitle', 'Désactiver cette annonce ?')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 2, fontSize: '0.9rem' }}>
            {t('listing.deactivate.confirmMessage', 'Vous pouvez réactiver votre annonce à tout moment.')}
          </Alert>

          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}>
            {listingToDeactivate?.title}
          </Typography>

          {/* Simple reason field */}
          <TextField
            fullWidth
            label={t('listing.deactivate.reasonLabel', 'Motif de désactivation')}
            placeholder={t('listing.deactivate.reasonPlaceholder', 'Ex: En attente de maintenance')}
            value={deactivateReasonText}
            onChange={(e) => setDeactivateReasonText(e.target.value)}
            multiline
            rows={3}
            variant="outlined"
            required
            error={!deactivateReasonText.trim() && submitting}
            helperText={!deactivateReasonText.trim() && submitting ? t('common.fieldRequired', 'Ce champ est obligatoire') : ''}
            sx={{ mt: 2 }}
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            onClick={closeDeactivateDialog} 
            disabled={submitting}
            sx={{ textTransform: 'none' }}
          >
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button
            onClick={handleDeactivateSubmit}
            color="warning"
            variant="contained"
            disabled={submitting || !deactivateReasonText.trim()}
            sx={{ 
              textTransform: 'none',
              minWidth: 120,
              background: 'linear-gradient(135deg, #ff9800, #fb8c00)'
            }}
          >
            {submitting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                {t('common.processing', 'Traitement...')}
              </>
            ) : t('listing.deactivate.confirm', 'Désactiver')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reactivation Confirmation Dialog */}
      <Dialog
        open={reactivateDialogOpen}
        onClose={closeReactivateDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{
          fontWeight: 'bold',
          fontSize: '1.1rem',
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          {t('listing.reactivate.confirmTitle', 'Réactiver cette annonce ?')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="success" sx={{ mb: 2, fontSize: '0.9rem' }}>
            {t('listing.reactivate.confirmMessage', 'Votre annonce sera de nouveau visible et disponible à la réservation.')}
          </Alert>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            {listingToReactivate?.title}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            onClick={closeReactivateDialog}
            disabled={submitting}
            sx={{ textTransform: 'none' }}
          >
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button
            onClick={handleReactivateSubmit}
            color="success"
            variant="contained"
            disabled={submitting}
            sx={{
              textTransform: 'none',
              minWidth: 120
            }}
          >
            {submitting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                {t('common.processing', 'Traitement...')}
              </>
            ) : t('listing.reactivate.confirm', 'Réactiver')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for mobile */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16, display: { sm: 'none' } }}
        onClick={() => setCreateChoiceModalOpen(true)}
      >
        <AddIcon />
      </Fab>

      {/* Modal de choix création */}
      <CreateChoiceModal 
        open={createChoiceModalOpen} 
        onClose={() => setCreateChoiceModalOpen(false)} 
      />

      {/* Sidebar de navigation Annonces / Produits */}
      <ListingProductSidebar activePage="listings" user={user} />
    </Box>
  );
};

export default MyListings;
